import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";
import { toBlob, toPng } from "html-to-image";

import { useLanguage } from "@/context/LanguageContext";
import { FULL_DECK, shuffleDeck, nameForLang } from "@/data/tarotDeck";
import { streamSSE } from "@/lib/sse";
import { playShuffle, playFlip, playChime, playCut, primeAudio, startAmbient, stopAmbient, duckAmbient, unduckAmbient } from "@/lib/sfx";
import { TarotCard } from "@/components/TarotCard";
import { ShareCard } from "@/components/ShareCard";
import { ShareCardStory } from "@/components/ShareCardStory";
import { SparkleShower } from "@/components/SparkleShower";
import { EmailMemoForm } from "@/components/EmailMemoForm";
import { DeckCutter } from "@/components/DeckCutter";
import { clearConsultant, hasDailyCardToday } from "@/lib/dailyCard";
import { READING } from "@/constants/testIds";
import { POSITION_KEYS, interp } from "@/i18n/translations";

const FIRST_NAME_RE = /^[A-Za-zÀ-ÖØ-öø-ÿ\-]{1,15}$/;
const FIRSTNAME_KEY = "arcana.firstName.v1";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Phases: "first_name" → "shuffle" → "consultant_drawn" → "question" → "cross_picking" → "cross_result"
const ReadingPage = () => {
  const { t, language } = useLanguage();
  const nav = useNavigate();

  // Site is entirely free — no auth, no daily limit.
  const hasReadingsLeft = true;
  const isAdmin = false;

  // Initial phase: ask for first name unless already stored in localStorage
  const initialFirstName = (typeof window !== "undefined" && window.localStorage.getItem(FIRSTNAME_KEY)) || "";
  const [firstName, setFirstName] = useState(initialFirstName);
  const [firstNameError, setFirstNameError] = useState("");

  const [phase, setPhase] = useState(initialFirstName ? "shuffle" : "first_name");
  const [deck, setDeck] = useState(() => shuffleDeck(FULL_DECK));
  const [shuffling, setShuffling] = useState(false);

  const [consultant, setConsultant] = useState(null); // card obj
  const [consultantInterp, setConsultantInterp] = useState("");
  const [consultantId, setConsultantId] = useState(null);
  const [streamingConsultant, setStreamingConsultant] = useState(false);

  const [question, setQuestion] = useState("");
  const [questionMode, setQuestionMode] = useState(null); // null | "custom" | "wisdom"

  // Cross state — 5 positions with cards placed in order
  const [crossPicks, setCrossPicks] = useState([]); // [{card, position}]
  const [cutAside, setCutAside] = useState([]); // cards set aside by the cuts
  const [cutting, setCutting] = useState(false);
  const [crossInterp, setCrossInterp] = useState("");
  const [crossId, setCrossId] = useState(null);
  const [streamingCross, setStreamingCross] = useState(false);

  const interpRef = useRef(null);
  const fiveCardsRef = useRef(null);
  const crossLayoutRef = useRef(null);
  const revealBtnRef = useRef(null);
  const shuffleBtnRef = useRef(null);
  const shareCardRef = useRef(null);
  const shareCardStoryRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [showEmailMemo, setShowEmailMemo] = useState(false);
  const [downloadingStory, setDownloadingStory] = useState(false);
  const [crossCreatedAt, setCrossCreatedAt] = useState(null);
  const [focusMode, setFocusMode] = useState(false);
  const [sparkle, setSparkle] = useState(false);

  // Toggle .focus-mode class on <body> so the site header can be hidden globally
  useEffect(() => {
    if (focusMode) document.body.classList.add("focus-mode");
    else document.body.classList.remove("focus-mode");
    return () => document.body.classList.remove("focus-mode");
  }, [focusMode]);

  // Auto-scroll into view when interpretation appears
  useEffect(() => {
    if ((streamingConsultant || streamingCross) && interpRef.current) {
      interpRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [streamingConsultant, streamingCross, consultantInterp, crossInterp]);

  const availableDeck = useMemo(() => {
    const excluded = new Set();
    if (consultant) excluded.add(consultant.key);
    for (const p of crossPicks) excluded.add(p.card.key);
    return deck.filter((c) => !excluded.has(c.key));
  }, [deck, consultant, crossPicks]);

  const remainingDeck = useMemo(() => {
    const aside = new Set(cutAside.map((c) => c.key));
    return availableDeck.filter((c) => !aside.has(c.key));
  }, [availableDeck, cutAside]);

  const currentPositionKey = POSITION_KEYS[crossPicks.length]; // next one to fill

  // Put the cursor on "Shuffle the deck" as soon as the reading opens.
  useEffect(() => {
    if (phase === "shuffle") shuffleBtnRef.current?.focus();
  }, [phase]);

  // Ambient music: start on mount, stop on unmount
  useEffect(() => {
    primeAudio();
    startAmbient();
    return () => stopAmbient(1.5);
  }, []);

  // Duck / unduck during streaming reveals
  useEffect(() => {
    if (streamingConsultant || streamingCross) duckAmbient();
    else unduckAmbient();
  }, [streamingConsultant, streamingCross]);

  // Auto-scroll to the consultant card + Ritual III block when Ritual III begins
  useEffect(() => {
    if (phase !== "cross_picking") return;
    if (crossPicks.length > 0) return; // only on entry, not on every pick
    const t = setTimeout(() => {
      const el = crossLayoutRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // Small headroom so the "Consultant" label sits just under the sticky header.
      const targetY = window.scrollY + rect.top - 90;
      window.scrollTo({ top: Math.max(0, targetY), behavior: "smooth" });
    }, 200);
    return () => clearTimeout(t);
  }, [phase, crossPicks.length]);

  // Once the 5th card has been placed, scroll back UP to the RÉVÉLER button
  // so the reader immediately sees the next action (which lives next to the consultant).
  useEffect(() => {
    if (phase !== "cross_picking") return;
    if (crossPicks.length !== 5) return;
    const t = setTimeout(() => {
      const el = revealBtnRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // Center the button vertically in the viewport for maximum clarity.
      const targetY = window.scrollY + rect.top - Math.max(120, window.innerHeight / 2 - 100);
      window.scrollTo({ top: Math.max(0, targetY), behavior: "smooth" });
    }, 350);
    return () => clearTimeout(t);
  }, [phase, crossPicks.length]);

  // Distinctive chime when it is time to pick the first card (each pick then chimes on its own)
  useEffect(() => {
    if (phase !== "cross_picking") return;
    if (crossPicks.length !== 0) return;
    playChime();
  }, [phase, crossPicks.length]);

  // -------- Actions --------
  const handleShuffle = () => {
    if (shuffling) return;
    primeAudio();
    playShuffle();
    setShuffling(true);
    setTimeout(() => {
      setDeck(shuffleDeck(FULL_DECK));
      setShuffling(false);
    }, 700);
  };

  const streamConsultant = async (cardKey) => {
    setStreamingConsultant(true);
    setConsultantInterp("");
    try {
      await streamSSE(
        `${API}/interpret_consultant`,
        { language, card_key: cardKey, first_name: firstName },
        (ev) => {
          if (ev.event === "start") setConsultantId(ev.id);
          else if (ev.event === "delta") setConsultantInterp((prev) => prev + ev.text);
          else if (ev.event === "error") throw new Error(ev.message);
        }
      );
    } catch (err) {
      console.error(err);
      if (err.status === 401) {
        nav("/", { replace: true });
      } else {
        toast.error(t.reading.failed);
      }
    } finally {
      setStreamingConsultant(false);
    }
  };

  const handleDrawConsultant = async () => {
    if (streamingConsultant) return;
    const picked = deck[Math.floor(Math.random() * deck.length)];
    setConsultant(picked);
    setPhase("consultant_drawn");
    await streamConsultant(picked.key);
  };

  // Once streaming completes — no journal to save (feature removed).
  useEffect(() => {}, [streamingConsultant]);

  const handleContinueToQuestion = () => {
    setQuestionMode(null);
    setQuestion("");
    setPhase("question");
  };
  const handleContinueToCross = () => setPhase("cross_picking");
  const handlePickCustom = () => setQuestionMode("custom");
  const handlePickWisdom = () => {
    setQuestionMode("wisdom");
    setQuestion("");
    setPhase("cross_picking");
  };
  const handleQuestionChange = (e) => {
    // Cap to 3 visual lines by limiting newlines and length (~180 chars is roughly 3 lines)
    let v = e.target.value;
    const parts = v.split("\n");
    if (parts.length > 3) v = parts.slice(0, 3).join("\n");
    if (v.length > 220) v = v.slice(0, 220);
    setQuestion(v);
  };

  const handlePickCrossCard = (card) => {
    if (streamingCross) return;
    if (crossPicks.length >= 5) return;
    if (crossPicks.find((p) => p.card.key === card.key)) return;
    playFlip();
    // A chime for every card placed in the cross — including the fifth.
    setTimeout(playChime, 180);
    const posKey = POSITION_KEYS[crossPicks.length];
    setCrossPicks((prev) => [...prev, { card, position: posKey }]);
    // Fire a discreet gold sparkle shower when the 5th (last) card is placed
    if (crossPicks.length === 4) {
      setTimeout(() => setSparkle((s) => !s), 250);
    }
  };

  // Ritual gesture: Space or Enter cuts the deck while the cross is being built.
  useEffect(() => {
    if (phase !== "cross_picking") return;
    const onKey = (e) => {
      if (e.key !== " " && e.key !== "Enter" && e.key !== "Spacebar") return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      // Let other focused buttons (reveal, restart…) keep their native Enter/Space behaviour.
      if (tag === "BUTTON" && e.target.getAttribute("data-testid") !== "reading-deck-cut-btn") return;
      e.preventDefault();
      handleCut();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Cut the deck: the top portion goes aside, the newly exposed card joins the cross.
  const handleCut = () => {
    if (cutting || streamingCross || crossPicks.length >= 5 || remainingDeck.length < 2) return;
    // Keep enough cards for the cuts still to come (each needs ≥ 2 cards), cut at most 14 at a time.
    const cutsLeftAfter = 4 - crossPicks.length;
    const kMax = Math.max(1, Math.min(14, remainingDeck.length - 1 - 2 * cutsLeftAfter));
    const k = 1 + Math.floor(Math.random() * kMax);
    const away = remainingDeck.slice(0, k);
    const picked = remainingDeck[k];
    setCutting(true);
    playCut();
    setTimeout(() => {
      setCutAside((prev) => [...prev, ...away]);
      handlePickCrossCard(picked);
      setCutting(false);
    }, 480);
  };

  const handleReveal = async () => {
    if (crossPicks.length !== 5 || streamingCross) return;
    // Staggered flip sounds mirroring the framer-motion delays (0, 0.12, 0.24, 0.36, 0.48s)
    POSITION_KEYS.forEach((_, i) => {
      setTimeout(() => playFlip(), 120 * i);
    });
    setStreamingCross(true);
    setCrossInterp("");
    setShowEmailMemo(false);
    setPhase("cross_result");

    let unclear = false;
    let firstChunk = "";
    try {
      await streamSSE(
        `${API}/interpret_cross`,
        {
          language,
          question: question.trim(),
          consultant_card_key: consultant.key,
          first_name: firstName,
          cards: crossPicks.map((p) => ({
            key: p.card.key,
            position: t.reading.posShort[p.position],
          })),
        },
        (ev) => {
          if (ev.event === "start") setCrossId(ev.id);
          else if (ev.event === "delta") {
            if (!unclear) {
              firstChunk += ev.text;
              if (firstChunk.trim().toUpperCase().startsWith("QUESTION_UNCLEAR")) {
                unclear = true;
                return; // discard rest of stream
              }
              setCrossInterp((prev) => prev + ev.text);
            }
          } else if (ev.event === "error") throw new Error(ev.message);
        }
      );
    } catch (err) {
      console.error(err);
      toast.error(t.reading.failed);
    } finally {
      setStreamingCross(false);
    }

    if (unclear) {
      // The oracle couldn't interpret the question — clear it and send the user back.
      setCrossInterp("");
      setShowEmailMemo(false);
      setQuestion("");
      setPhase("question");
      toast.error(t.reading.questionUnclear);
      // Bring the (now-empty) question textarea into view.
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    } else {
      // Scroll down to the newly-streamed reading result ("Lecture") section.
      setTimeout(() => {
        const el = document.querySelector('[data-testid="reading-result-section"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  };

  // Cross reading completed — set creation timestamp for export. No journal saving.
  useEffect(() => {
    if (streamingCross || !crossInterp || crossPicks.length !== 5) return;
    setCrossCreatedAt(new Date().toISOString());
  }, [streamingCross]);

  const handleEndSession = () => {
    // Wipe every trace of the consultant so the next visitor starts fresh.
    clearConsultant();
    nav("/", { replace: true });
  };

  const handleRestart = () => {
    setConsultant(null);
    setConsultantInterp("");
    setConsultantId(null);
    setQuestion("");
    setCrossPicks([]);
    setCutAside([]);
    setCrossInterp("");
    setShowEmailMemo(false);
    setCrossId(null);
    setDeck(shuffleDeck(FULL_DECK));
    setPhase(firstName ? "shuffle" : "first_name");
  };

  const handleNewQuestion = () => {
    // Keep consultant + its interpretation, reshuffle the rest
    setQuestion("");
    setCrossPicks([]);
    setCutAside([]);
    setCrossInterp("");
    setShowEmailMemo(false);
    setCrossId(null);
    setDeck(shuffleDeck(FULL_DECK));
    setPhase("question");
  };

  const handleCopy = async () => {
    const text = [
      `${t.reading.consultantIs} ${consultant ? nameForLang(consultant, language) : ""}`,
      "",
      consultantInterp,
      "",
      "—",
      "",
      question ? `${t.reading.questionLabel}: ${question}` : "",
      "",
      ...crossPicks.map(
        (p) => `${t.reading.posShort[p.position]} — ${nameForLang(p.card, language)}`
      ),
      "",
      crossInterp,
    ]
      .filter(Boolean)
      .join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t.reading.copied);
    } catch {
      toast.error(t.reading.failed);
    }
  };

  const handlePrint = () => window.print();

  const memoPayload = () => ({
    language,
    first_name: firstName,
    consultant_card: consultant ? nameForLang(consultant, language) : "",
    consultant_interpretation: consultantInterp,
    question,
    cards: crossPicks.map((p) => ({ position: t.reading.posShort[p.position], name: nameForLang(p.card, language) })),
    interpretation: crossInterp,
  });

  const handleDownloadPng = async () => {
    if (!shareCardRef.current || downloading) return;
    setDownloading(true);
    const toastId = toast.loading(t.reading.downloading);
    try {
      // Give the browser a moment to render web fonts inside the node
      await document.fonts?.ready;
      const dataUrl = await toPng(shareCardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: "#F4EAD1",
      });
      const link = document.createElement("a");
      link.download = `arcana-mystique-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = dataUrl;
      link.click();
      toast.success(t.reading.downloaded, { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error(t.reading.failed, { id: toastId });
    } finally {
      setDownloading(false);
    }
  };

  const handleShareImage = async () => {
    if (!shareCardRef.current || sharing) return;
    setSharing(true);
    const toastId = toast.loading(t.reading.sharing);
    try {
      await document.fonts?.ready;
      const blob = await toBlob(shareCardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: "#F4EAD1",
      });
      const file = new File([blob], `arcana-mystique-${new Date().toISOString().slice(0, 10)}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: t.reading.shareTitle, text: t.reading.shareText });
        toast.success(t.reading.shared, { id: toastId });
      } else {
        // No native share sheet (desktop) — fall back to a download.
        const link = document.createElement("a");
        link.download = file.name;
        link.href = URL.createObjectURL(blob);
        link.click();
        URL.revokeObjectURL(link.href);
        toast.success(t.reading.downloaded, { id: toastId });
      }
    } catch (err) {
      if (err?.name === "AbortError") {
        toast.dismiss(toastId);
      } else {
        console.error(err);
        toast.error(t.reading.failed, { id: toastId });
      }
    } finally {
      setSharing(false);
    }
  };

  const handleDownloadStory = async () => {
    if (!shareCardStoryRef.current || downloadingStory) return;
    setDownloadingStory(true);
    const toastId = toast.loading(t.reading.downloading);
    try {
      await document.fonts?.ready;
      const dataUrl = await toPng(shareCardStoryRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: "#F4EAD1",
      });
      const link = document.createElement("a");
      link.download = `arcana-mystique-story-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = dataUrl;
      link.click();
      toast.success(t.reading.downloaded, { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error(t.reading.failed, { id: toastId });
    } finally {
      setDownloadingStory(false);
    }
  };

  return (
    <main
      data-testid={READING.root}
      className={`relative max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16 ${
        focusMode ? "min-h-screen pt-6 md:pt-8" : "min-h-[calc(100vh-72px)]"
      }`}
    >
      {/* Focus-mode entry (visible when NOT in focus) */}
      {!focusMode && (
        <button
          type="button"
          onClick={() => setFocusMode(true)}
          data-testid={READING.focusToggleBtn}
          title={t.reading.focus}
          className="absolute top-6 right-6 md:top-10 md:right-10 z-30 inline-flex items-center gap-2 px-3 py-2 rounded-full border border-gold/40 text-ivory/70 hover:text-gold hover:border-gold transition-colors duration-300 text-[0.65rem] tracking-[0.3em] uppercase"
        >
          <Maximize2 size={14} />
          {t.reading.focus}
        </button>
      )}
      {/* Focus-mode exit (visible only when in focus) */}
      {focusMode && (
        <button
          type="button"
          onClick={() => setFocusMode(false)}
          data-testid={READING.focusExitBtn}
          title={t.reading.exitFocus}
          className="fixed top-4 right-4 md:top-6 md:right-6 z-50 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-ivory text-oxblood hover:bg-[#3D3462] hover:text-oxblood transition-colors duration-300 text-[0.65rem] tracking-[0.3em] uppercase shadow-lg"
        >
          <Minimize2 size={14} />
          {t.reading.exitFocus}
        </button>
      )}

      <PhaseBreadcrumb phase={phase} />

      <SparkleShower show={sparkle} />

      {/* Daily limit reached — friendly block, no purchase option (site is free) */}
      {!hasReadingsLeft && phase !== "cross_result" && (
        <div
          data-testid={READING.outOfCreditsBanner}
          className="mb-10 rounded-md border border-gold/40 bg-oxblood-surface/70 p-6 md:p-8"
        >
          <div className="text-sm md:text-base tracking-[0.4em] uppercase text-[#14103A] font-bold mb-3">
            ✧ {t.usage.limitReachedTitle}
          </div>
          <p className="text-ivory/80 font-serif italic leading-relaxed">
            {t.usage.limitReachedBody}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* LEFT PANEL */}
        <aside className="lg:col-span-4 space-y-8 print:hidden">
          {phase === "first_name" && (
            <PhaseCard title={t.reading.phase.firstName} idx="0">
              <p className="text-ivory font-sans font-bold text-base md:text-lg leading-relaxed">
                {t.reading.firstNamePrompt}
              </p>
              <input
                type="text"
                data-testid={READING.firstNameInput}
                value={firstName}
                onChange={(e) => {
                  const v = e.target.value.slice(0, 15);
                  setFirstName(v);
                  setFirstNameError("");
                }}
                placeholder={t.reading.firstNamePlaceholder}
                maxLength={15}
                className="mt-4 w-full bg-oxblood-surface border border-gold/25 rounded-md px-4 py-3 font-serif text-ivory placeholder-ivory/30 focus:outline-none focus:border-gold/70 transition-colors duration-300"
              />
              {firstNameError && (
                <p className="mt-2 text-sm text-red-300 font-serif italic">{firstNameError}</p>
              )}
              <button
                type="button"
                data-testid={READING.firstNameContinue}
                onClick={() => {
                  if (!FIRST_NAME_RE.test(firstName)) {
                    setFirstNameError(t.reading.firstNameError);
                    return;
                  }
                  setFirstNameError("");
                  window.localStorage.setItem(FIRSTNAME_KEY, firstName.trim());
                  setPhase("shuffle");
                }}
                disabled={!firstName || !hasReadingsLeft}
                className="mt-6 inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gold text-oxblood hover:bg-gold-soft transition-colors duration-300 text-[0.75rem] tracking-[0.35em] uppercase font-semibold disabled:opacity-40"
              >
                {t.reading.firstNameContinue} →
              </button>
            </PhaseCard>
          )}

          {(phase === "shuffle" || phase === "consultant_drawn") && hasReadingsLeft && (
            <PhaseCard title={t.reading.phase.consultant} idx="I">
              {firstName && (
                <p
                  data-testid="reading-consultant-name"
                  className="text-[0.72rem] tracking-[0.35em] uppercase text-gold/90 -mt-3 mb-2"
                >
                  {t.reading.consultantLabel} · <span className="font-serif italic text-ivory text-base normal-case tracking-normal">{firstName}</span>
                </p>
              )}
              <p className="text-ivory font-sans font-bold text-base md:text-lg leading-relaxed">
                {language === "fr"
                  ? "Brassez les cartes, puis tirez votre carte personnelle."
                  : "Shuffle the deck, then draw your personal card."}
              </p>
              <div className="flex flex-wrap gap-3 pt-2">
                <button
                  ref={shuffleBtnRef}
                  onClick={handleShuffle}
                  data-testid={READING.shuffleBtn}
                  disabled={shuffling || streamingConsultant || phase === "consultant_drawn"}
                  className="px-5 py-2.5 rounded-full border border-gold/40 text-ivory hover:border-gold hover:text-gold transition-colors duration-300 text-[0.7rem] tracking-[0.3em] uppercase disabled:opacity-40"
                >
                  {shuffling ? t.reading.shuffling : t.reading.shuffle}
                </button>
                <button
                  onClick={handleDrawConsultant}
                  data-testid={READING.drawConsultantBtn}
                  disabled={streamingConsultant || phase === "consultant_drawn" || !hasReadingsLeft}
                  className="px-5 py-2.5 rounded-full bg-gold text-oxblood hover:bg-gold-soft transition-colors duration-300 text-[0.7rem] tracking-[0.3em] uppercase font-semibold disabled:opacity-40"
                >
                  {streamingConsultant ? t.reading.drawing : t.reading.drawConsultant}
                </button>
              </div>
              {/* The "CONTINUER →" transition button is now rendered below the
                  consultant interpretation stream, as its natural next step. */}
            </PhaseCard>
          )}

          {/* Phase "question" no longer renders its own PhaseCard on the left —
              the Ritual II content is now injected directly below the consultant
              interpretation as its natural continuation. */}

          {(phase === "cross_picking" || phase === "cross_result") && (
            <>
              {/* In cross_picking phase the ritual header is now rendered next to the
                  consultant card (inside CrossLayout). In cross_result we keep the
                  end-of-session actions here for easy access. */}
              {phase === "cross_result" && (
                <PhaseCard title={t.reading.phase.cross} idx="III">
                  <div className="space-y-4">
                    <p className="text-ivory/70 font-sans font-light leading-relaxed">
                      {language === "fr"
                        ? "La croix est révélée. L'oracle relie les cartes en une histoire cohérente ci-dessous."
                        : "The cross is revealed. Below, the oracle weaves the cards into one coherent story."}
                    </p>
                    <div className="flex flex-wrap gap-3 pt-2">
                      <button
                        onClick={handleNewQuestion}
                        data-testid={READING.newQuestionBtn}
                        className="px-5 py-2.5 rounded-full bg-gold text-oxblood hover:bg-gold-soft transition-colors duration-300 text-[0.7rem] tracking-[0.3em] uppercase font-semibold"
                      >
                        {t.reading.newQuestion}
                      </button>
                      <button
                        type="button"
                        onClick={handleEndSession}
                        data-testid={READING.endBtn}
                        className="px-5 py-2.5 rounded-full border border-gold/40 text-ivory/80 hover:border-gold hover:text-gold transition-colors duration-300 text-[0.7rem] tracking-[0.3em] uppercase"
                      >
                        {t.reading.endSession}
                      </button>
                      {!hasDailyCardToday(firstName) && (
                        <button
                          type="button"
                          onClick={() => nav("/daily")}
                          data-testid={READING.dailyCardBtn}
                          className="px-5 py-2.5 rounded-full border border-gold/40 text-gold hover:bg-gold hover:text-oxblood transition-colors duration-300 text-[0.7rem] tracking-[0.3em] uppercase"
                        >
                          ✧ {t.reading.dailyCardBtn}
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-3 pt-2">
                      <button
                        onClick={handleCopy}
                        data-testid={READING.copyBtn}
                        className="px-4 py-2 text-[0.7rem] tracking-[0.3em] uppercase text-ivory/60 hover:text-gold transition-colors duration-300"
                      >
                        {t.reading.copy}
                      </button>
                      <button
                        onClick={handleShareImage}
                        disabled={sharing || streamingCross}
                        data-testid={READING.shareImageBtn}
                        className="px-4 py-2 text-[0.7rem] tracking-[0.3em] uppercase text-gold hover:text-gold-soft transition-colors duration-300 disabled:opacity-40"
                      >
                        ✦ {sharing ? t.reading.sharing : t.reading.shareImage}
                      </button>
                      <button
                        onClick={handleDownloadPng}
                        disabled={downloading || streamingCross}
                        data-testid={READING.downloadPngBtn}
                        className="px-4 py-2 text-[0.7rem] tracking-[0.3em] uppercase text-ivory/60 hover:text-gold transition-colors duration-300 disabled:opacity-40"
                      >
                        {downloading ? t.reading.downloading : t.reading.downloadImage}
                      </button>
                      <button
                        onClick={handleDownloadStory}
                        disabled={downloadingStory || streamingCross}
                        data-testid={READING.downloadStoryBtn}
                        className="px-4 py-2 text-[0.7rem] tracking-[0.3em] uppercase text-ivory/60 hover:text-gold transition-colors duration-300 disabled:opacity-40"
                      >
                        {downloadingStory ? t.reading.downloading : t.reading.downloadStory}
                      </button>
                      <button
                        onClick={handlePrint}
                        data-testid={READING.printBtn}
                        className="px-4 py-2 text-[0.7rem] tracking-[0.3em] uppercase text-ivory/60 hover:text-gold transition-colors duration-300"
                      >
                        {t.reading.print}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowEmailMemo((v) => !v)}
                        disabled={streamingCross}
                        data-testid={READING.emailMemoBtn}
                        className="px-4 py-2 text-[0.7rem] tracking-[0.3em] uppercase text-ivory/60 hover:text-gold transition-colors duration-300 disabled:opacity-40"
                      >
                        ✉ {t.reading.emailMemo}
                      </button>
                    </div>
                    {showEmailMemo && !streamingCross && (
                      <EmailMemoForm payload={memoPayload()} onClose={() => setShowEmailMemo(false)} />
                    )}
                  </div>
                  <button
                    onClick={handleRestart}
                    data-testid={READING.restartBtn}
                    className="block mt-4 text-[0.7rem] tracking-[0.28em] uppercase text-ivory/40 hover:text-gold transition-colors duration-300"
                  >
                    ↺ {t.reading.restart}
                  </button>
                </PhaseCard>
              )}
            </>
          )}

          {/* Interpretation stream */}
          {(consultantInterp || streamingConsultant) && (
            <div ref={interpRef} className="border-t border-gold/20 pt-6">
              <div className="text-sm md:text-base tracking-[0.35em] uppercase text-[#14103A] font-bold mb-3">
                ✦ {t.reading.consultantInterp}
              </div>
              <StreamingText
                text={consultantInterp}
                streaming={streamingConsultant}
                testId={READING.consultantInterp}
              />
              {/* "L'oracle réfléchit…" placeholder — shown while awaiting the very
                  first delta of the interpretation stream. */}
              {streamingConsultant && !consultantInterp && (
                <div
                  data-testid="reading-consultant-thinking"
                  className="flex items-center gap-4 text-ivory font-serif italic font-bold text-2xl md:text-3xl leading-snug"
                >
                  <span className="pulse-square" />
                  <span>{t.reading.thinking}</span>
                </div>
              )}
              {/* Natural next-step CTA: after the consultant interpretation, invite
                  the reader to progress toward the question (Ritual II). */}
              {phase === "consultant_drawn" && !streamingConsultant && (
                <button
                  onClick={handleContinueToQuestion}
                  data-testid={READING.continueToQuestion}
                  className="mt-8 inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gold text-oxblood hover:bg-gold-soft transition-colors duration-300 text-base md:text-lg tracking-[0.28em] uppercase font-bold group"
                >
                  {t.reading.continueToQuestion}
                  <span className="text-2xl md:text-3xl leading-none transition-transform duration-300 group-hover:translate-x-1">
                    →
                  </span>
                </button>
              )}
              {/* Ritual II — Question — inlined right after the consultant reading */}
              {phase === "question" && (
                <div className="mt-10 pt-6 border-t border-gold/20 space-y-4">
                  <div className="text-base md:text-lg tracking-[0.35em] uppercase text-[#14103A] font-bold">
                    ✧ RITUAL · II
                  </div>
                  <h2 className="font-serif font-light italic text-3xl md:text-4xl text-ivory leading-tight">
                    {t.reading.phase.question}
                  </h2>
                  {questionMode === null && (
                    <>
                      <p className="text-ivory font-serif italic font-bold text-lg md:text-xl leading-snug pt-2">
                        {t.reading.chooseQuestionMode}
                      </p>
                      <div className="mt-4 flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={handlePickCustom}
                          data-testid={READING.questionCustomBtn}
                          className="px-5 py-3 rounded-full bg-gold text-oxblood hover:bg-gold-soft transition-colors duration-300 text-[0.7rem] tracking-[0.3em] uppercase font-semibold"
                        >
                          {t.reading.questionCustomBtn}
                        </button>
                        <button
                          onClick={handlePickWisdom}
                          data-testid={READING.questionWisdomBtn}
                          className="px-5 py-3 rounded-full border border-gold/40 text-ivory hover:border-gold hover:text-gold transition-colors duration-300 text-[0.7rem] tracking-[0.3em] uppercase"
                        >
                          {t.reading.questionWisdomBtn}
                        </button>
                      </div>
                    </>
                  )}
                  {questionMode === "custom" && (
                    <>
                      <label
                        htmlFor="q"
                        className="block text-[0.75rem] md:text-sm tracking-[0.3em] uppercase text-gold font-bold mb-2 pt-2"
                      >
                        {t.reading.questionLabel}
                      </label>
                      <textarea
                        id="q"
                        data-testid={READING.questionInput}
                        value={question}
                        onChange={handleQuestionChange}
                        placeholder={t.reading.questionPlaceholder}
                        rows={3}
                        maxLength={220}
                        className="w-full resize-none bg-oxblood-surface border border-gold/25 rounded-md px-4 py-3 font-serif italic text-ivory placeholder-ivory/30 focus:outline-none focus:border-gold/70 transition-colors duration-300"
                      />
                      <div className="mt-4 flex flex-wrap gap-3">
                        <button
                          onClick={handleContinueToCross}
                          data-testid={READING.continueToCross}
                          disabled={!question.trim()}
                          className="px-5 py-2.5 rounded-full bg-gold text-oxblood hover:bg-gold-soft transition-colors duration-300 text-[0.7rem] tracking-[0.3em] uppercase font-semibold disabled:opacity-30"
                        >
                          {t.reading.continueToCross}
                        </button>
                        <button
                          onClick={() => setQuestionMode(null)}
                          className="px-4 py-2 text-[0.7rem] tracking-[0.3em] uppercase text-ivory/50 hover:text-gold transition-colors duration-300"
                        >
                          ← {t.reading.restart}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </aside>

        {/* RIGHT PANEL — Cross visual + deck */}
        <section ref={crossLayoutRef} className="lg:col-span-8 space-y-14">
          {/* Consultant card + Cross layout */}
          <CrossLayout
            consultant={consultant}
            consultantRevealed={phase !== "shuffle" && phase !== "first_name"}
            picks={crossPicks}
            revealed={phase === "cross_result"}
            fiveCardsRef={fiveCardsRef}
            ritualIII={
              phase === "cross_picking" ? (
                <div className="mb-8 space-y-4">
                  <div className="text-base md:text-lg tracking-[0.35em] uppercase text-[#14103A] font-bold">
                    ✧ RITUAL · III
                  </div>
                  <h2 className="font-serif font-light italic text-3xl md:text-4xl text-ivory leading-tight">
                    {t.reading.phase.cross}
                  </h2>

                  <p className="pt-3 text-center text-[#14103A] font-sans font-bold text-sm md:text-base tracking-[0.18em] uppercase leading-snug">
                    {t.reading.ritualIIIIntro}
                  </p>

                  <ul className="pt-1 space-y-1.5 text-ivory/85 font-sans text-sm md:text-base leading-relaxed">
                    {POSITION_KEYS.map((k) => (
                      <li key={k} className="flex gap-2">
                        <span className="text-gold font-bold">·</span>
                        <span>
                          <span className="font-bold text-ivory">{t.reading.positions[k]}</span>
                          <span className="text-ivory/70"> — {t.reading.posDescriptions[k]}</span>
                        </span>
                      </li>
                    ))}
                  </ul>

                  <div className="pt-4">
                    <DeckCutter
                      remaining={remainingDeck.length}
                      cutAside={cutAside.length}
                      cuts={crossPicks.length}
                      cutting={cutting}
                      onCut={handleCut}
                      disabled={streamingCross}
                    />
                  </div>

                  <div className="pt-4 flex flex-wrap items-center justify-center gap-4">
                    {crossPicks.length === 5 && (
                      <button
                        ref={revealBtnRef}
                        onClick={handleReveal}
                        data-testid={READING.revealBtn}
                        className="px-6 py-3 rounded-full bg-gold text-oxblood hover:bg-gold-soft transition-colors duration-300 text-sm md:text-base tracking-[0.3em] uppercase font-bold shadow-[0_0_28px_8px_rgba(212,175,55,0.35)]"
                      >
                        {streamingCross ? t.reading.revealing : t.reading.reveal}
                      </button>
                    )}
                    <button
                      onClick={handleRestart}
                      data-testid={READING.restartBtn}
                      className="text-[0.7rem] tracking-[0.28em] uppercase text-ivory/50 hover:text-gold transition-colors duration-300"
                    >
                      ↺ {t.reading.restart}
                    </button>
                  </div>
                </div>
              ) : null
            }
          />

          {/* Cross interpretation stream */}
          {(crossInterp || streamingCross) && phase === "cross_result" && (
            <div
              data-testid="reading-result-section"
              className="border-t border-gold/20 pt-8 scroll-mt-24"
            >
              <h2 className="font-serif italic text-4xl md:text-5xl font-semibold text-ivory mb-6 leading-tight">
                ✦ {t.reading.phase.result}
              </h2>
              <StreamingText
                text={crossInterp}
                streaming={streamingCross}
                testId={READING.crossInterp}
                large
                showSignature
              />
              {/* "L'oracle réfléchit…" placeholder while awaiting the first delta */}
              {streamingCross && !crossInterp && (
                <div
                  data-testid="reading-cross-thinking"
                  className="flex items-center gap-4 text-ivory font-serif italic font-bold text-2xl md:text-3xl leading-snug"
                >
                  <span className="pulse-square" />
                  <span>{t.reading.thinking}</span>
                </div>
              )}
              {!streamingCross && crossInterp && (
                <div className="mt-10 flex justify-center">
                  <button
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    data-testid={READING.backToTopBtn}
                    className="inline-flex items-center gap-3 px-8 py-4 rounded-full border-2 border-[#14103A] text-ivory hover:bg-[#14103A] hover:text-oxblood transition-colors duration-300 text-sm md:text-base tracking-[0.28em] uppercase font-bold"
                  >
                    <span className="text-xl md:text-2xl leading-none">↑</span>
                    {t.reading.backToTop}
                  </button>
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      {/* Off-screen ShareCard for PNG export */}
      {phase === "cross_result" && crossInterp && !streamingCross && (
        <div
          aria-hidden="true"
          style={{
            position: "fixed",
            top: 0,
            left: "-10000px",
            pointerEvents: "none",
            zIndex: -1,
          }}
        >
          <ShareCard
            ref={shareCardRef}
            consultant={consultant}
            question={question.trim()}
            picks={crossPicks}
            interpretation={crossInterp}
            createdAt={crossCreatedAt}
          />
          <ShareCardStory
            ref={shareCardStoryRef}
            consultant={consultant}
            question={question.trim()}
            picks={crossPicks}
            interpretation={crossInterp}
            createdAt={crossCreatedAt}
          />
        </div>
      )}
    </main>
  );
};

// ---------- Sub-components ----------
const PhaseCard = ({ title, idx, children }) => (
  <div className="space-y-4">
    <div>
      <div className="text-base md:text-lg tracking-[0.35em] uppercase text-[#14103A] font-bold mb-3">
        ✧ RITUAL · {idx}
      </div>
      <h1 className="font-serif font-light italic text-3xl md:text-4xl text-ivory leading-tight">
        {title}
      </h1>
    </div>
    {children}
  </div>
);

const PhaseBreadcrumb = ({ phase }) => {
  const { t } = useLanguage();
  const label =
    phase === "first_name"
      ? t.reading.phase.firstName
      : phase === "shuffle" || phase === "consultant_drawn"
      ? t.reading.phase.consultant
      : phase === "question"
      ? t.reading.phase.question
      : phase === "cross_picking"
      ? t.reading.phase.cross
      : t.reading.phase.result;
  return (
    <div className="mb-8 flex items-center gap-3 text-[0.7rem] tracking-[0.32em] uppercase text-ivory/50 print:hidden">
      <Link
        to="/"
        data-testid={READING.backHome}
        className="hover:text-gold transition-colors duration-300"
      >
        {t.nav.home}
      </Link>
      <span className="text-gold/40">·</span>
      <Link
        to="/welcome"
        data-testid="reading-back-to-ritual"
        className="hover:text-gold transition-colors duration-300"
      >
        {t.home.ritual}
      </Link>
      <span className="text-gold/40">·</span>
      <span className="text-gold/80">{label}</span>
    </div>
  );
};

const HEADING_RE = /^(synthèse globale|overall synthesis|réponse à votre question|answer to your question)$/i;

const extractSignature = (raw) => {
  if (!raw) return { signature: "", body: "" };
  const m = raw.match(/^\s*SIGNATURE\s*[:：]\s*(.+?)(?:\r?\n|$)/i);
  if (!m) return { signature: "", body: raw };
  return {
    signature: m[1].trim(),
    body: raw.slice(m[0].length).replace(/^\s+/, ""),
  };
};

const renderMarkdown = (text) => {
  if (!text) return null;
  const out = [];
  const re = /\*\*([^*]+?)\*\*/g;
  let idx = 0;
  let m;
  let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > idx) out.push(text.slice(idx, m.index));
    const inner = m[1].trim();
    if (HEADING_RE.test(inner)) {
      out.push(
        <strong
          key={k++}
          className="block mt-8 mb-3 font-serif italic text-2xl md:text-3xl font-bold text-ivory"
        >
          {m[1]}
        </strong>
      );
    } else {
      out.push(
        <strong key={k++} className="font-bold text-ivory text-[1.05em]">
          {m[1]}
        </strong>
      );
    }
    idx = re.lastIndex;
  }
  if (idx < text.length) out.push(text.slice(idx));
  return out;
};

const StreamingText = ({ text, streaming, testId, large = false, showSignature = false }) => {
  const { signature, body } = showSignature ? extractSignature(text) : { signature: "", body: text };
  return (
    <div
      data-testid={testId}
      className={`font-serif text-ivory/90 leading-relaxed whitespace-pre-wrap ${
        large ? "text-base md:text-lg" : "text-base"
      }`}
    >
      {signature && (
        <div className="mb-8 pb-5 border-b-2 border-[#14103A]/25">
          <p className="font-serif italic text-2xl md:text-3xl lg:text-4xl font-bold text-[#14103A] leading-tight">
            « {signature} »
          </p>
        </div>
      )}
      {renderMarkdown(body)}
      {streaming && body && (
        <span className="pulse-square ml-2" />
      )}
      {!text && streaming && (
        <div className="flex items-center gap-3">
          <span className="pulse-square" />
          <span className="text-ivory/60 italic">{"…"}</span>
        </div>
      )}
    </div>
  );
};

const CrossSlot = ({ posKey, picks, revealed }) => {
  const { t } = useLanguage();
  const byPos = Object.fromEntries(picks.map((p) => [p.position, p]));
  const p = byPos[posKey];
  const isNext = !p && picks.length === POSITION_KEYS.indexOf(posKey);
  return (
    <div
      data-testid={READING.slot(posKey)}
      className="flex flex-col items-center gap-2"
    >
      {p ? (
        <motion.div
          initial={{ opacity: 0, y: -60, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col items-center gap-2"
        >
          <TarotCard
            card={p.card}
            revealed={revealed}
            size="md"
            label={t.reading.positions[posKey]}
            index={POSITION_KEYS.indexOf(posKey)}
          />
          {revealed && (
            <p className="max-w-[10rem] md:max-w-[12rem] text-center text-[0.72rem] md:text-[0.8rem] leading-snug text-ivory/85 font-serif italic">
              {t.reading.posDescriptions[posKey]}
            </p>
          )}
        </motion.div>
      ) : (
        <>
          <span className={`text-[0.72rem] md:text-[0.82rem] tracking-[0.28em] uppercase font-bold font-sans text-center ${
            isNext ? "text-gold" : "text-[#14103A]"
          }`}>
            {t.reading.positions[posKey]}
          </span>
          <div className={`w-24 h-40 md:w-28 md:h-44 rounded-md border border-dashed flex items-center justify-center ${
            isNext ? "border-gold pulse-frame" : "border-gold/25"
          }`}>
            <span className={`rune text-2xl ${isNext ? "text-gold opacity-90" : "opacity-40"}`}>✷</span>
          </div>
        </>
      )}
    </div>
  );
};

const CrossLayout = ({ consultant, consultantRevealed, picks, revealed, fiveCardsRef, ritualIII }) => {
  const { t } = useLanguage();

  return (
    <div className="flex flex-col md:flex-row gap-10 md:gap-14 items-start">
      {/* Consultant column */}
      <div className="flex flex-col items-center gap-3">
        <span className="text-[0.65rem] md:text-[0.7rem] tracking-[0.32em] uppercase text-gold font-sans">
          {t.history.consultantLabel}
        </span>
        {consultant ? (
          <TarotCard
            card={consultant}
            revealed={consultantRevealed}
            size="lg"
            glow
            testId={READING.consultantCard}
          />
        ) : (
          <div className="w-32 h-52 md:w-40 md:h-64 rounded-md border border-dashed border-gold/25 flex items-center justify-center">
            <span className="rune text-3xl opacity-40">☾</span>
          </div>
        )}
      </div>

      {/* Cross layout */}
      <div className="flex-1 w-full">
        {ritualIII}
        {consultantRevealed && (
          <h2
            ref={fiveCardsRef}
            data-testid="reading-five-cards-title"
            className="mb-6 md:mb-8 text-center text-base md:text-lg lg:text-xl tracking-[0.32em] uppercase font-bold text-[#14103A] scroll-mt-32"
          >
            {t.reading.fiveCardsTitle}
          </h2>
        )}
        <div className="grid grid-cols-3 grid-rows-3 gap-4 md:gap-6 justify-items-center items-center min-h-[440px] md:min-h-[520px]">
          <div />
          <CrossSlot posKey="top" picks={picks} revealed={revealed} />
          <div />

          <CrossSlot posKey="left" picks={picks} revealed={revealed} />
          <CrossSlot posKey="center" picks={picks} revealed={revealed} />
          <CrossSlot posKey="right" picks={picks} revealed={revealed} />

          <div />
          <CrossSlot posKey="bottom" picks={picks} revealed={revealed} />
          <div />
        </div>
      </div>
    </div>
  );
};

export default ReadingPage;
